import { NameAndUrl } from "csi/comm/JsonResponse";
import { ColorSpec } from "designSystem/components/colorchip/ColorChipArrayStore";
import { EMPTY_URL } from "csi/constants";
import ExtendedPath from "csi/core/ExtendedPath";
import NodeDataStore from "csi/datastore/NodeDataStore";
import locale from "csi/locale";
import { getRuntimeRulesFromNode, getRuntimeRulesQuery, RuntimeRulesSchema } from "csi/middleware/attributeRule/RuleService";
import { C8Node, cnl, ICache } from "csi/typings";
import xml from "csi/xml";
import Query from "csi/xml/Query";
import queryProcessor from "csi/xml/QueryProcessor";
import _ from "lodash";
import { action, computed, makeObservable, observable, runInAction } from "mobx";
import { createContext, useContext } from "react";
import ProductType from "src/Apparel/ProductType";
import {
	defaultColorUsedInPresentation,
	getColorMatrixPath,
	getPresentationPrimaryType,
	getProductColorSpecImage,
	getSetsProductUsedIn,
	PRESENTATION_RULE
} from "../PPService";

export type AddToSetCallback = (productUrl: cnl, colorUrl?: cnl, skipAddingItem?: boolean) => void;
export type RemoveCallback = (productUrl: cnl) => Promise<void>;
export type EditSetCallback = (setUrl: cnl) => Promise<void>;

export default class ProductGalleryStore {
	private readonly _colorQuery: Query;

	/** callback to edit set */
	readonly editSetCallback: EditSetCallback;

	/** indicates if button to add to set is shown */
	readonly onAddToSet?: AddToSetCallback;

	/** indicates if action to remove product is shown */
	readonly onRemove?: RemoveCallback;

	/** presentation url containing gallery */
	readonly presentationUrl?: cnl;

	/** indicates if attributes should be shown in gallery */
	readonly showAttributes: boolean;

	/** data store */
	readonly store: NodeDataStore;

	/** cache containing products */
	get cache(): ICache {
		return this.store.cache;
	}

	@observable.ref private _setLists: NameAndUrl[];

	/** matrix path from product type to its colors */
	@computed get colorMatrixPath(): ExtendedPath | null {
		const { presentation } = this;

		if (!presentation) {
			return null;
		}

		const primaryType = getPresentationPrimaryType(presentation);
		return getColorMatrixPath(primaryType);
	}

	/** query to apply to colors for each product */
	@computed get colorQuery(): Query | null {
		const { runtimeRules, colorMatrixPath, _colorQuery } = this;

		// for the new presentation wizard, the rule node is not created yet, just use the query user types in directly
		return _colorQuery || getRuntimeRulesQuery(runtimeRules, colorMatrixPath);
	}

	/** presentation node */
	@computed get presentation(): C8Node | null {
		return this.cache.get(this.presentationUrl);
	}

	/** menu sections  */
	get setLists(): NameAndUrl[] {
		return this._setLists;
	}

	/** rules for presentation */
	@computed get runtimeRules(): RuntimeRulesSchema | null {
		const { cache, colorMatrixPath, presentation } = this;

		if (!presentation) {
			return null;
		}

		const ruleNode = cache.get(presentation[PRESENTATION_RULE]);
		return ruleNode ? getRuntimeRulesFromNode(ruleNode, colorMatrixPath) : null;
	}

	/**
	 * Return color specs for given product; if color given, then return spec only for that color
	 * NOTE: requires ProductColor and ColorSpecification nodes to already be cached
	 */
	getColorSpecs = (product: C8Node, productHasViolation: boolean, colorUrl?: cnl): ColorSpec[] => {
		const { cache, colorQuery, presentationUrl } = this;

		if (colorUrl === EMPTY_URL) {
			return [];
		}

		const colorUrls = colorUrl ? [colorUrl] : (product.ProductColors ?? []);
		const allColorNodes = cache.getNodes(colorUrls);
		const normalColorNodes = colorQuery ? queryProcessor.applyQueryToNodes(colorQuery, allColorNodes, cache) : allColorNodes;

		// get color spec
		const _getColorSpec = (color: C8Node, hasViolation: boolean): ColorSpec => {
			const image = getProductColorSpecImage(color, cache);
			const name = locale.getNodeConfiguredName(color, cache);

			return {
				hasViolation,
				id: color.$URL,
				image,
				name,
			};
		};

		// step 1. include the normal colors
		let colorSpecs: ColorSpec[];

		if (productHasViolation) {
			// in the case of violation, only need to show the used ones, and mark them invalid
			colorSpecs = normalColorNodes.filter((color) => color.Presentations.includes(presentationUrl)).map((color) => _getColorSpec(color, true));
		} else {
			colorSpecs = normalColorNodes.map((color) => _getColorSpec(color, false));
		}

		// step2. include the colors with violations which used in sets
		const inValidColorNodes = _.difference(allColorNodes, normalColorNodes);
		const violationColors = inValidColorNodes.filter((node) => node.Presentations.includes(presentationUrl));

		for (const color of violationColors) {
			colorSpecs.push(_getColorSpec(color, true));
		}

		return _.sortBy(colorSpecs, ["name"]);
	};

	/** return color spec for colors in the case of violation prediction */
	getColorSpecsForViolationPrediction = (product: C8Node, productHasViolation: boolean, colorViolationUrls: Set<cnl>): ColorSpec[] => {
		const { cache } = this;
		const colorUrls = product.ProductColors ?? [];
		const allColorNodes = cache.getNodes(colorUrls);
		const colorSpecs: ColorSpec[] = [];

		// get color spec
		const _getColorSpec = (color: C8Node, hasViolation: boolean): ColorSpec => {
			const image = getProductColorSpecImage(color, cache);
			const name = locale.getNodeConfiguredName(color, cache);

			return {
				hasViolation,
				id: color.$URL,
				image,
				name,
			};
		};

		if (productHasViolation) {
			for (const color of allColorNodes) {
				colorSpecs.push(_getColorSpec(color, true));
			}
		} else {
			// has violation due to color, only make those colors invalid
			for (const color of allColorNodes) {
				colorSpecs.push(_getColorSpec(color, colorViolationUrls.has(color.$URL)));
			}
		}

		return _.sortBy(colorSpecs, ["name"]);
	};

	/** return color spec representing Product default image rather than a ProductColor image */
	getDefaultColorSpec = (product: C8Node, productHasViolation: boolean, colors: ColorSpec[], colorUrl?: cnl): ColorSpec | null => {
		// already specify the color, then don't need to show
		if (xml.isValidURL(colorUrl)) {
			return null;
		}

		const { cache, presentation } = this;

		const defaultSpec = {
			id: product.$URL,
			name: locale.resource("Default"),
		};

		const defaultViolationSpec = {
			...defaultSpec,
			hasViolation: true,
		};

		const allColorsHasViolation = colors.length > 0 && colors.every((color) => color.hasViolation);

		// in the case of violation
		if (allColorsHasViolation || productHasViolation) {
			// check if default one is used in Set
			const defaultIsUsedInSetUnderTheCurrentPP = defaultColorUsedInPresentation(cache, presentation, product);

			if (xml.isEmptyURL(colorUrl) && defaultIsUsedInSetUnderTheCurrentPP) {
				// definitely it's being used in this case, then show it
				return defaultViolationSpec;
			}

			return null;
		}

		return defaultSpec;
	};

	/** return color spec for the default color in the case of violation prediction */
	getDefaultColorSpecForViolationPrediction = (product: C8Node, productHasViolation: boolean, colors: ColorSpec[]): ColorSpec => {
		const defaultSpec = {
			id: product.$URL,
			name: locale.resource("Default"),
		};

		const defaultViolationSpec = {
			...defaultSpec,
			hasViolation: true,
		};

		const allColorsHasViolation = colors.length > 0 && colors.every((color) => color.hasViolation);

		// in the case of violation
		if (allColorsHasViolation || productHasViolation) {
			return defaultViolationSpec;
		}

		return defaultSpec;
	};

	/** return node for given color spec, which could be the product itself if it is the default color spec */
	getImageNode = (selectedColor: ColorSpec): C8Node => {
		return this.cache.get(selectedColor?.id);
	};

	/** handler when opens up the menu on the card */
	@action onOpenGalleryCardMenu = async (product: C8Node): Promise<void> => {
		const { presentationUrl } = this;
		let sets;

		if (presentationUrl) {
			// if the presentation doesn't exist yet, no need to query
			sets = await getSetsProductUsedIn(presentationUrl, product.$URL);
		}

		runInAction(() => {
			this._setLists = sets;
		});
	};

	/** return true if given product supports color */
	supportsColor = (product: C8Node): boolean => {
		return ProductType.hasColor(product);
	};

	constructor(
		store: NodeDataStore,
		showAttributes: boolean,
		colorQuery?: Query,
		presentationUrl?: cnl,
		onAddToSet?: AddToSetCallback,
		onRemove?: RemoveCallback,
		editSetCallback?: EditSetCallback,
	) {
		makeObservable(this);
		this._colorQuery = colorQuery;
		this.presentationUrl = presentationUrl;
		this.showAttributes = showAttributes;
		this.store = store;
		this.onAddToSet = onAddToSet;
		this.onRemove = onRemove;
		this.editSetCallback = editSetCallback;
	}
}

export const ProductGalleryStoreContext = createContext<ProductGalleryStore>(undefined);
export const useProductGalleryStore = (): ProductGalleryStore => useContext(ProductGalleryStoreContext);